import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FileUpload } from "./FileUpload";
import { useState } from "react";


const caseStudySchema = z.object({
  client_name: z.string().min(1, "Client name is required"),
  industry: z.string().optional(),
  slug: z.string().min(1, "Slug is required"),
  challenge_dk: z.string().min(1, "Danish challenge is required"),
  challenge_en: z.string().min(1, "English challenge is required"),
  solution_dk: z.string().min(1, "Danish solution is required"),
  solution_en: z.string().min(1, "English solution is required"),
  results_dk: z.string().min(1, "Danish results are required"),
  results_en: z.string().min(1, "English results are required"),
  testimonial_dk: z.string().optional(),
  testimonial_en: z.string().optional(),
  images: z.array(z.string()).optional(),
  featured_image: z.string().optional(),
  published: z.boolean().default(false),
});

type CaseStudyFormValues = z.infer<typeof caseStudySchema>;

interface CaseStudyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CaseStudyFormValues) => void;
  initialData?: any;
  isLoading?: boolean;
}

export const CaseStudyForm = ({ open, onOpenChange, onSubmit, initialData, isLoading }: CaseStudyFormProps) => {
  const [images, setImages] = useState<string[]>(initialData?.images || []);
  const [featuredImage, setFeaturedImage] = useState<string | undefined>(initialData?.featured_image);

  const form = useForm<CaseStudyFormValues>({
    resolver: zodResolver(caseStudySchema),
    defaultValues: initialData || {
      client_name: "",
      industry: "",
      slug: "",
      challenge_dk: "",
      challenge_en: "",
      solution_dk: "",
      solution_en: "",
      results_dk: "",
      results_en: "",
      testimonial_dk: "",
      testimonial_en: "",
      images: [],
      featured_image: "",
      published: false,
    },
  });

  const handleFormSubmit = (data: CaseStudyFormValues) => {
    onSubmit({
      ...data,
      images,
      featured_image: featuredImage,
    });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit Case Study" : "Create Case Study"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name</FormLabel>
                    <FormControl>
                      <Input 
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!initialData) {
                            form.setValue('slug', generateSlug(e.target.value));
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="challenge_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Challenge (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="challenge_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Challenge (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="solution_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Solution (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="solution_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Solution (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="results_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Results (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="results_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Results (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="testimonial_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Testimonial (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="testimonial_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Testimonial (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">Images</h3>
              
              <FileUpload
                label="Featured Image"
                bucket="case-studies"
                accept="image/*"
                maxSizeMB={5}
                currentFile={featuredImage}
                onFileUploaded={(url) => setFeaturedImage(url)}
                onFileRemoved={() => setFeaturedImage(undefined)}
              />

              <FileUpload
                label="Additional Images / PDF"
                bucket="case-studies"
                accept="image/*,application/pdf"
                maxSizeMB={10}
                currentFile={images[0]}
                onFileUploaded={(url) => setImages([...images, url])}
                onFileRemoved={() => setImages(images.slice(0, -1))}
              />
            </div>

            <FormField
              control={form.control}
              name="published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Published</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : initialData ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
